import React from 'react'
import { Box, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import { __t } from '../../../../i18n/t'
import { DiscountKind, NAME_MAX, ReviewBlock, ReviewChip, ReviewTone, StepKey, checks, nameValue, perDayOn, reviewSections, sentenceRich, singleWindow, stepKeys, typeInfo } from '../model'
import { useWiz } from '../state/WizardContext'
import { Ask, Callout, FieldError, Hint, Rich } from '../ui/basics'
import { LabeledInput, TextButton } from '../ui/controls'
import { C, CALLOUT, TYPE_STYLE } from '../ui/tokens'

const TONE = { ok: 'success', warn: 'warn', info: 'info' } as const

// Estilo fora do JSX: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver ui/tokens.ts).
const sentenceBoxSx = { display: 'flex', flexDirection: 'column', gap: 1, p: '18px 20px', borderRadius: '14px', bgcolor: C.surface, border: `1px solid ${C.line}` } as const
const sentenceLabelSx = { fontSize: 12.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted } as const
const sentenceHeadSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 } as const
const badgeSx = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999, px: 1, py: '2px' } as const
const nameBoxSx = { display: 'flex', flexDirection: 'column', gap: 0.75 } as const
const checksSx = { display: 'flex', flexDirection: 'column', gap: 1 } as const
const sectionsSx = { display: 'flex', flexDirection: 'column', gap: 1.5 } as const
const sectionSx = { border: `1px solid ${C.line}`, borderRadius: '12px', p: '14px 18px', display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const sectionHeadSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 } as const
const lineSx = { display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(120px,190px) minmax(0,1fr)' }, gap: { xs: 0.5, sm: 1.5 }, alignItems: 'center', fontSize: 14, lineHeight: 1.45 } as const
const lineKeySx = { color: C.muted } as const
const chipsSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75, minWidth: 0 } as const
const noteSx = { gridColumn: { sm: 2 }, fontSize: 12.5, color: C.muted } as const

/** Cores das etiquetas: público e postos no violeta da tela, efeito bom em verde, informação em azul, atenção em laranja. */
const TONE_SX: Record<ReviewTone, { bgcolor: string; color: string }> = {
  neutral: { bgcolor: '#f1f5f9', color: '#334155' },
  brand: { bgcolor: C.chipBg, color: C.violet },
  good: { bgcolor: '#dcfce7', color: '#166534' },
  info: { bgcolor: '#e0f2fe', color: '#075985' },
  warn: { bgcolor: CALLOUT.warnBg, color: CALLOUT.warnText },
}
const chipBaseSx = { display: 'inline-flex', alignItems: 'center', minHeight: 28, px: 1.25, py: 0.25, borderRadius: 999, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' } as const

// Tabela dos combustíveis (como no cadastro antigo): nome, valor por litro em destaque e o custo em pontos.
const fuelsBoxSx = { border: `1px solid ${C.line}`, borderRadius: '10px', overflow: 'hidden', maxWidth: 640 } as const
const fuelGridCost = { display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) 84px 96px', sm: 'minmax(0,1fr) 150px 130px' }, gap: { xs: 1, sm: 1.5 }, alignItems: 'center', px: 1.75, py: 1 } as const
const fuelGridNoCost = { display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) 84px', sm: 'minmax(0,1fr) 150px' }, gap: { xs: 1, sm: 1.5 }, alignItems: 'center', px: 1.75, py: 1 } as const
const fuelHeadSx = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, bgcolor: C.surface } as const
const fuelRowSx = { borderTop: `1px solid ${C.lineSoft}`, fontSize: 14.5 } as const
const fuelNameSx = { color: C.ink, fontWeight: 500, overflowWrap: 'anywhere' } as const
const fuelOrSx = { color: C.muted, fontSize: 13, pl: 1.5 } as const
const valueSx = { justifySelf: 'start', px: 1.25, py: 0.25, borderRadius: '8px', bgcolor: '#dcfce7', color: '#166534', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' } as const

// Notificação num cartão simples, do jeito que o cliente lê: título, texto e, embaixo, a mensagem ao tocar.
const pushSx = { display: 'flex', gap: 1.25, alignItems: 'flex-start', p: '12px 14px', borderRadius: '10px', border: `1px solid ${C.line}`, bgcolor: C.surface } as const
const pushTextSx = { display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, fontSize: 14, color: C.ink, lineHeight: 1.45, overflowWrap: 'anywhere' } as const
const pushBodySx = { mt: 0.5, pt: 0.75, borderTop: `1px solid ${C.line}`, fontSize: 13, color: C.muted } as const

const Chip: React.FC<{ chip: ReviewChip }> = ({ chip }) => <Box component="span" sx={[chipBaseSx, TONE_SX[chip.tone]]}>{chip.text}</Box>

const Block: React.FC<{ block: ReviewBlock }> = ({ block }) => {
  if (block.kind === 'fuels') {
    const grid = block.showCost ? fuelGridCost : fuelGridNoCost
    return (
      <Box role="table" aria-label={__t('Desconto por combustível')} sx={fuelsBoxSx}>
        <Box role="row" sx={[grid, fuelHeadSx]}>
          <span role="columnheader">{__t('Combustível')}</span>
          <span role="columnheader">{__t('Desconto por litro')}</span>
          {block.showCost ? <span role="columnheader">{__t('Custo')}</span> : null}
        </Box>
        {block.rows.map((r, i) => (
          <Box key={i} role="row" sx={[grid, fuelRowSx]}>
            <Box component="span" role="cell" sx={r.first ? fuelNameSx : fuelOrSx}>{r.first ? r.fuel : __t('ou')}</Box>
            <Box component="span" role="cell" sx={valueSx}>{r.value}</Box>
            {r.cost ? <Box component="span" role="cell"><Chip chip={r.cost} /></Box> : null}
          </Box>
        ))}
      </Box>
    )
  }
  if (block.kind === 'push') {
    return (
      <Box sx={pushSx}>
        <NotificationsActiveOutlinedIcon aria-hidden sx={{ color: C.violet, flexShrink: 0, mt: '2px' }} />
        <Box sx={pushTextSx}>
          <b>{block.title}</b>
          <span>{block.text}</span>
          <Box component="span" sx={pushBodySx}>{__t('Ao tocar: %{mensagem}', { mensagem: block.body })}</Box>
        </Box>
      </Box>
    )
  }
  return (
    <Box sx={lineSx}>
      <Box component="span" sx={lineKeySx}>{block.k}</Box>
      <Box sx={chipsSx}>{block.chips.map((x, i) => <Chip key={i} chip={x} />)}</Box>
      {block.note ? <Box component="span" sx={noteSx}>{block.note}</Box> : null}
    </Box>
  )
}
const lockSx = { display: 'flex', gap: 1, alignItems: 'flex-start', fontSize: 13.5, color: C.muted, lineHeight: 1.45 } as const

/** Etapa final: a frase, o nome, o que conferir antes de salvar e cada seção com "Alterar". */
export const StepReview: React.FC = () => {
  const { c, d, set, fe, goStep } = useWiz()
  const special = d.kind === DiscountKind.special
  const showName = !!d.kind && d.kind !== DiscountKind.default
  const sections = reviewSections(d, c)
  const keys = stepKeys(d.kind)
  const perDay = perDayOn(d)
  const list = checks(d, c)
  const optional = <Box component="span" sx={{ fontWeight: 400, color: C.muted }}> {__t('(opcional)')}</Box>
  return (
    <>
      <Box sx={sentenceBoxSx}>
        <Box sx={sentenceHeadSx}>
          <Box component="span" sx={sentenceLabelSx}>{__t('Como vai funcionar')}</Box>
          {d.kind ? <Box component="span" sx={[badgeSx, { color: TYPE_STYLE[d.kind].ink, bgcolor: TYPE_STYLE[d.kind].tint }]}>{typeInfo(d.kind).badge}</Box> : null}
        </Box>
        <Typography component="div" sx={{ fontSize: 16, lineHeight: 1.6, color: C.ink, overflowWrap: 'anywhere' }}><Rich text={sentenceRich(d, c)} /></Typography>
      </Box>

      {showName ? (
        <Box sx={nameBoxSx}>
          <LabeledInput
            id="f-name"
            label={<Box component="span" sx={{ fontSize: 14.5, fontWeight: 500, color: C.ink }}>{__t('Nome do desconto')}{special ? null : optional}</Box>}
            width={420}
            maxLength={NAME_MAX}
            value={nameValue(d, c)}
            onChange={e => set(x => ({ ...x, name: e.target.value, nameEdited: true }))}
            invalid={!!fe['f-name']}
            describedBy={['n-name-help', fe['f-name'] ? 'n-name-err' : ''].filter(Boolean).join(' ')}
          />
          <Hint id="n-name-help" sx={{ fontSize: 12.5 }}>{__t('O cliente não vê este nome.')}</Hint>
          {fe['f-name'] ? <FieldError id="n-name-err">{fe['f-name']}</FieldError> : null}
        </Box>
      ) : null}

      {list.length ? (
        <Ask id="h-checks" title={__t('Antes de salvar')}>
          <Box sx={checksSx}>
            {list.map((x, i) => (
              <Callout key={i} tone={TONE[x.tone]} sx={{ fontSize: 14 }}>{x.text}</Callout>
            ))}
          </Box>
        </Ask>
      ) : null}

      <Box sx={sectionsSx}>
        {sections.map(sec => (
          <Box key={sec.step} sx={sectionSx}>
            <Box sx={sectionHeadSx}>
              <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{sec.title}</Typography>
              <TextButton onClick={() => goStep(keys.indexOf(sec.step))} icon={<EditIcon />} ariaLabel={sec.editLabel}>{__t('Alterar')}</TextButton>
            </Box>
            {sec.blocks.map((b, i) => <Block key={i} block={b} />)}
            {sec.step === StepKey.when && perDay ? (
              <TextButton
                onClick={() => { set(singleWindow); goStep(keys.indexOf(StepKey.when)) }}
                sx={{ alignSelf: 'flex-start' }}
              >
                {__t('Usar um horário só para todos os dias')}
              </TextButton>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box sx={lockSx}>
        <LockOutlinedIcon aria-hidden sx={{ fontSize: 18, flexShrink: 0, mt: '1px' }} />
        <span>{__t('Depois de salvar não dá para editar. Para mudar, duplique o desconto e desative o antigo.')}</span>
      </Box>
    </>
  )
}
